/**
 * SHARED VALIDATION RULES
 * Single source of truth for schedule validation across all components
 * Used by: ExcelValidator (CreatePaymentNew) and ValidatorPage
 */

import { scheduleHeaders } from '../ScheduleFields';
import { FieldDefinition, ValidationError, RowValidationFunction, GlobalValidationFunction } from '../ExcelValidatorTool/lib/types';

/**
 * Helper function to parse money values
 */
const parseMoneyValue = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;
  const str = String(value).replace(/,/g, '');
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

/**
 * Helper function to convert month name to number
 */
const getMonthNumber = (monthName: string): number => {
  const months: Record<string, number> = {
    'January': 1, 'February': 2, 'March': 3, 'April': 4,
    'May': 5, 'June': 6, 'July': 7, 'August': 8,
    'September': 9, 'October': 10, 'November': 11, 'December': 12
  };
  return months[monthName] || 0;
};

/**
 * Helper function to convert month number to name
 */
const getMonthName = (monthNum: number): string => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  return months[monthNum - 1] || '';
};

/**
 * Creates validation rules for schedule fields
 * @param pfaCodes - List of valid PFA codes from API
 */
export const createValidationRules = (pfaCodes: string[] = []): FieldDefinition[] => {
  return [
    {
      label: scheduleHeaders.employerCode,
      key: scheduleHeaders.employerCode,
      validations: [
        { rule: 'required', errorMessage: 'Employer code is required', level: 'error' },
        // Regex validation is commented out - uncomment if needed
        // {
        //   rule: 'regex',
        //   errorMessage: 'Employer code must be alphanumeric with a maximum of 12 characters',
        //   level: 'error',
        //   value: '^[A-Za-z0-9]{1,12}$'
        // }
      ],
    },
    {
      label: scheduleHeaders.month,
      key: scheduleHeaders.month,
      validations: [
        { rule: 'required', errorMessage: 'Month is required', level: 'error' },
        {
          rule: 'regex',
          errorMessage: 'Month must be a valid month name',
          level: 'error',
          value: '^(January|February|March|April|May|June|July|August|September|October|November|December)$'
        }
      ],
    },
    {
      label: scheduleHeaders.year,
      key: scheduleHeaders.year,
      validations: [
        { rule: 'required', errorMessage: 'Year is required', level: 'error' },
        {
          rule: 'regex',
          errorMessage: 'Year must be 4 digits (YYYY)',
          level: 'error',
          value: '^\\d{4}$'
        }
      ],
    },
    {
      label: scheduleHeaders.staffId,
      key: scheduleHeaders.staffId,
      validations: [
        { rule: 'required', errorMessage: 'Staff ID is required', level: 'error' },
        {
          rule: 'regex',
          errorMessage: 'Staff ID must be alphanumeric',
          level: 'error',
          value: '^[A-Za-z0-9_./ -]+$'
        }
      ],
    },
    {
      label: scheduleHeaders.rsapin,
      key: scheduleHeaders.rsapin,
      validations: [
        { rule: 'required', errorMessage: 'RSA Pin is required', level: 'error' },
        {
          rule: 'regex',
          errorMessage: 'RSA Pin must be 15 characters starting with TPEN or PEN',
          level: 'error',
          value: '^(TPEN\\d{11}|PEN\\d{12})$'
        }
      ],
    },
    {
      label: scheduleHeaders.employeeName,
      key: scheduleHeaders.employeeName,
      validations: [
        { rule: 'required', errorMessage: 'Employee name is required', level: 'error' },
        {
          rule: 'regex',
          errorMessage: 'Employee name must be in order: Surname, First Name & Other Name. No special characters except hyphen (-) and apostrophes.',
          level: 'error',
          value: "^[A-Za-zÀ-ÿ'’., -]{2,120}$"
        }
      ],
    },
    {
      label: scheduleHeaders.normalContributionEmployee,
      key: scheduleHeaders.normalContributionEmployee,
      validations: [
        { rule: 'required', errorMessage: 'Normal contribution (employee) is required', level: 'error' },
        {
          rule: 'regex',
          errorMessage: 'Normal contribution must be numeric and non-negative',
          level: 'error',
          value: '^(?!-)(?:\\d{1,3}(?:,\\d{3})*|\\d+)(?:\\.\\d+)?$'
        }
      ],
    },
    {
      label: scheduleHeaders.normalContributionEmployer,
      key: scheduleHeaders.normalContributionEmployer,
      validations: [
        { rule: 'required', errorMessage: 'Normal contribution (employer) is required', level: 'error' },
        {
          rule: 'regex',
          errorMessage: 'Normal contribution (employer) must be numeric and non-negative',
          level: 'error',
          value: '^(?!-)(?:\\d{1,3}(?:,\\d{3})*|\\d+)(?:\\.\\d+)?$'
        }
      ],
    },
    {
      label: scheduleHeaders.voluntaryContributionEmployee,
      key: scheduleHeaders.voluntaryContributionEmployee,
      validations: [
        {
          rule: 'regex',
          errorMessage: 'Voluntary contribution (employee) must be numeric or empty',
          level: 'error',
          value: '^(?:\\d{1,3}(?:,\\d{3})*(?:\\.\\d+)?|\\d+(?:\\.\\d+)?)?$'
        }
      ],
    },
    {
      label: scheduleHeaders.voluntaryContributionEmployer,
      key: scheduleHeaders.voluntaryContributionEmployer,
      validations: [
        {
          rule: 'regex',
          errorMessage: 'Voluntary contribution (employer) must be numeric or empty',
          level: 'error',
          value: '^(?:\\d{1,3}(?:,\\d{3})*(?:\\.\\d+)?|\\d+(?:\\.\\d+)?)?$'
        }
      ],
    },
    {
      label: scheduleHeaders.otherContribution,
      key: scheduleHeaders.otherContribution,
      validations: [
        {
          rule: 'regex',
          errorMessage: 'Other contribution must be a non-negative number or empty',
          level: 'error',
          value: '^(?:\\d{1,3}(?:,\\d{3})*(?:\\.\\d+)?|\\d+(?:\\.\\d+)?)?$'
        }
      ],
    },
    {
      label: scheduleHeaders.total,
      key: scheduleHeaders.total,
      validations: [
        { rule: 'required', errorMessage: 'Total amount is required', level: 'error' },
        {
          rule: 'regex',
          errorMessage: 'Total amount must be numeric and non-negative',
          level: 'error',
          value: '^(?!-)(?:\\d{1,3}(?:,\\d{3})*|\\d+)(?:\\.\\d+)?$'
        }
      ],
    },
    {
      label: scheduleHeaders.pfaCode,
      key: scheduleHeaders.pfaCode,
      validations: [
        { rule: 'required', errorMessage: 'PFA code is required', level: 'error' },
        // Only validate against PFA code list if codes are available (API succeeded)
        ...(pfaCodes.length > 0 ? [{
          rule: 'regex' as const,
          errorMessage: 'This field must be a valid PFA code',
          level: 'error' as const,
          value: `^(${pfaCodes.join('|')})$`
        }] : [])
      ],
    },
  ];
};

/**
 * Creates row-level validation functions
 * These validate relationships between columns in the same row
 */
export const createRowValidations = (): RowValidationFunction[] => {
  return [
    // Validate date: cannot be less than July 2004
    (row, rowIndex): ValidationError[] => {
      const errors: ValidationError[] = [];
      const month = row[scheduleHeaders.month];
      const year = row[scheduleHeaders.year];

      if (month && year) {
        const yearNum = parseInt(String(year));
        const monthNum = getMonthNumber(String(month));

        if (yearNum < 2004 || (yearNum === 2004 && monthNum < 7)) {
          errors.push({
            cellRef: '',
            columnKey: scheduleHeaders.year,
            rowIndex,
            errorMessage: `Date cannot be less than July 2004. Found: ${month} ${year}`,
            cellValue: year
          });
          errors.push({
            cellRef: '',
            columnKey: scheduleHeaders.month,
            rowIndex,
            errorMessage: `Date cannot be less than July 2004. Found: ${month} ${year}`,
            cellValue: month
          });
        }
      }

      return errors;
    },

    // Validate date: cannot be ahead of current month and year
    (row, rowIndex): ValidationError[] => {
      const errors: ValidationError[] = [];
      const month = row[scheduleHeaders.month];
      const year = row[scheduleHeaders.year];

      if (month && year) {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;

        const yearNum = parseInt(String(year));
        const monthNum = getMonthNumber(String(month));

        if (yearNum > currentYear || (yearNum === currentYear && monthNum > currentMonth)) {
          errors.push({
            cellRef: '',
            columnKey: scheduleHeaders.year,
            rowIndex,
            errorMessage: `Date cannot be ahead of current month and year (${getMonthName(currentMonth)} ${currentYear}). Found: ${month} ${year}`,
            cellValue: year
          });
          errors.push({
            cellRef: '',
            columnKey: scheduleHeaders.month,
            rowIndex,
            errorMessage: `Date cannot be ahead of current month and year (${getMonthName(currentMonth)} ${currentYear}). Found: ${month} ${year}`,
            cellValue: month
          });
        }
      }

      return errors;
    },

    // Validate employee name format: must contain at least surname and first name, can contain max 3 parts (excluding title)
    (row, rowIndex): ValidationError[] => {
      const errors: ValidationError[] = [];

      const titles = new Set([
        "mr", "mrs", "miss", "ms", "dr",
        "prof", "engr", "chief", "hon"
      ]);

      const raw = String(row[scheduleHeaders.employeeName] || "").trim();
      if (!raw) return errors;

      const parts = raw.split(/\s+/);

      // check if first word is title
      const first = parts[0].replace(/\./g, "").toLowerCase();

      let nameParts = parts;

      if (titles.has(first)) {
        nameParts = parts.slice(1);
      }

      if (nameParts.length < 2 || nameParts.length > 3) {
        errors.push({
          cellRef: "",
          columnKey: scheduleHeaders.employeeName,
          rowIndex,
          errorMessage:
            "Employee name must contain 2–3 names (title not counted).",
          cellValue: raw
        });
      }

      return errors;
    },

    // Validate that normal contribution employee does not exceed employer contribution
    // Unless employer contribution is 0 (then employee can be any value or equal)
    (row, rowIndex): ValidationError[] => {
      const errors: ValidationError[] = [];

      const normalEmployee = parseMoneyValue(row[scheduleHeaders.normalContributionEmployee]);
      const normalEmployer = parseMoneyValue(row[scheduleHeaders.normalContributionEmployer]);

      // Employee cannot be greater than employer, UNLESS employer is 0
      if (normalEmployee > normalEmployer && normalEmployer !== 0) {
        errors.push({
          cellRef: '',
          columnKey: scheduleHeaders.normalContributionEmployee,
          rowIndex,
          errorMessage: `Normal contribution (employee) cannot exceed normal contribution (employer). Employee: ${normalEmployee.toFixed(2)}, Employer: ${normalEmployer.toFixed(2)}`,
          cellValue: row[scheduleHeaders.normalContributionEmployee]
        });
      }

      return errors;
    },

    // Validate that total amount equals sum of contributions
    (row, rowIndex): ValidationError[] => {
      const errors: ValidationError[] = [];
      const totalAmount = parseMoneyValue(row[scheduleHeaders.total]);
      const normalEmployee = parseMoneyValue(row[scheduleHeaders.normalContributionEmployee]);
      const normalEmployer = parseMoneyValue(row[scheduleHeaders.normalContributionEmployer]);
      const voluntaryEmployee = parseMoneyValue(row[scheduleHeaders.voluntaryContributionEmployee]);
      const voluntaryEmployer = parseMoneyValue(row[scheduleHeaders.voluntaryContributionEmployer]);
      const otherContribution = parseMoneyValue(row[scheduleHeaders.otherContribution]);

      const calculatedTotal = normalEmployee + normalEmployer + voluntaryEmployee + voluntaryEmployer + otherContribution;

      if (Math.abs(totalAmount - calculatedTotal) > 0.01) {
        errors.push({
          cellRef: '',
          columnKey: scheduleHeaders.total,
          rowIndex,
          errorMessage: `Total amount (${totalAmount.toFixed(2)}) does not match sum of contributions (${calculatedTotal.toFixed(2)})`,
          cellValue: row[scheduleHeaders.total]
        });
      }

      return errors;
    },
  ];
};

/**
 * Creates global validation functions
 * These validate relationships across all rows
 */
export const createGlobalValidations = (expectedEmployerCode?: string): GlobalValidationFunction[] => {
  return [
    // Validate employer codes: use source of truth if available, otherwise check all codes are the same
    (allRows): ValidationError[] => {
      const errors: ValidationError[] = [];
      if (allRows.length === 0) return errors;

      const hasSourceOfTruth = expectedEmployerCode && expectedEmployerCode.trim().length > 0;

      if (hasSourceOfTruth) {
        const expectedCode = expectedEmployerCode!.trim();
        allRows.forEach((row, index) => {
          const code = row[scheduleHeaders.employerCode];
          if (code && String(code).trim() !== expectedCode) {
            errors.push({
              cellRef: '',
              columnKey: scheduleHeaders.employerCode,
              rowIndex: index,
              errorMessage: `Employer code must be "${expectedCode}". Found: "${String(code).trim()}"`,
              cellValue: code
            });
          }
        });
      } else {
        const employerCodes = allRows.map(row => row[scheduleHeaders.employerCode]);
        const uniqueCodes = Array.from(new Set(employerCodes.filter(Boolean)));
        if (uniqueCodes.length > 1) {
          allRows.forEach((row, index) => {
            if (row[scheduleHeaders.employerCode]) {
              errors.push({
                cellRef: '',
                columnKey: scheduleHeaders.employerCode,
                rowIndex: index,
                errorMessage: `All employer codes must be the same. Found: ${uniqueCodes.join(', ')}`,
                cellValue: row[scheduleHeaders.employerCode]
              });
            }
          });
        }
      }
      return errors;
    },

    // Validate single year: schedule must not contain more than one year
    // DISABLED: backend now accepts multiple years per row, each beneficiary carries its own month/year
    // (allRows): ValidationError[] => {
    //   const errors: ValidationError[] = [];
    //   if (allRows.length === 0) return errors;
    //
    //   const years = allRows
    //     .map(row => String(row[scheduleHeaders.year] || '').trim())
    //     .filter(Boolean);
    //   const uniqueYears = Array.from(new Set(years));
    //
    //   if (uniqueYears.length > 1) {
    //     const referenceYear = uniqueYears[0];
    //     allRows.forEach((row, index) => {
    //       const rowYear = String(row[scheduleHeaders.year] || '').trim();
    //       if (rowYear && rowYear !== referenceYear) {
    //         errors.push({
    //           cellRef: '',
    //           columnKey: scheduleHeaders.year,
    //           rowIndex: index,
    //           errorMessage: `Schedule must not contain more than one year. Found "${rowYear}" but expected "${referenceYear}". Years present: ${uniqueYears.join(', ')}.`,
    //           cellValue: row[scheduleHeaders.year]
    //         });
    //       }
    //     });
    //   }
    //
    //   return errors;
    // },

    // Validate RSA PIN consistency: same PIN must have same name and staff ID
    (allRows): ValidationError[] => {
      const errors: ValidationError[] = [];
      const rsaPinGroups = new Map<string, { rowIndex: number; name: string; staffId: string; period: string; rsaPin: string }[]>();

      allRows.forEach((row, index) => {
        const rsaPin = row[scheduleHeaders.rsapin];
        if (rsaPin && String(rsaPin).trim()) {
          const group = rsaPinGroups.get(rsaPin) || [];
          group.push({
            rowIndex: index,
            name: String(row[scheduleHeaders.employeeName] || '').trim(),
            staffId: String(row[scheduleHeaders.staffId] || '').trim(),
            period: `${row[scheduleHeaders.month]}-${row[scheduleHeaders.year]}`,
            rsaPin
          });
          rsaPinGroups.set(rsaPin, group);
        }
      });

      rsaPinGroups.forEach((group, rsaPin) => {
        if (group.length <= 1) return;

        const firstEntry = group[0];
        const periodMap = new Map<string, number>();
        periodMap.set(firstEntry.period, firstEntry.rowIndex);
        const inconsistentRows = new Set<number>();

        for (let i = 1; i < group.length; i++) {
          const currentEntry = group[i];
          let hasError = false;

          if (currentEntry.name !== firstEntry.name) {
            hasError = true;
            errors.push({
              cellRef: '',
              columnKey: scheduleHeaders.employeeName,
              rowIndex: currentEntry.rowIndex,
              errorMessage: `Name mismatch for duplicate RSA PIN ${rsaPin}. Expected "${firstEntry.name}", found "${currentEntry.name}".`,
              cellValue: currentEntry.name
            });
          }

          if (currentEntry.staffId !== firstEntry.staffId) {
            hasError = true;
            errors.push({
              cellRef: '',
              columnKey: scheduleHeaders.staffId,
              rowIndex: currentEntry.rowIndex,
              errorMessage: `Staff ID mismatch for duplicate RSA PIN ${rsaPin}. Expected "${firstEntry.staffId}", found "${currentEntry.staffId}".`,
              cellValue: currentEntry.staffId
            });
          }

          if (hasError) inconsistentRows.add(currentEntry.rowIndex);

          const existingRowIndex = periodMap.get(currentEntry.period);
          if (existingRowIndex !== undefined) {
            errors.push({
              cellRef: '',
              columnKey: scheduleHeaders.rsapin,
              rowIndex: currentEntry.rowIndex,
              errorMessage: `Duplicate RSA PIN ${rsaPin} for same period (${currentEntry.period.replace('-', ' ')}) as row ${existingRowIndex + 2}.`,
              cellValue: rsaPin
            });
            errors.push({
              cellRef: '',
              columnKey: scheduleHeaders.rsapin,
              rowIndex: existingRowIndex,
              errorMessage: `Duplicate RSA PIN ${rsaPin} conflict with row ${currentEntry.rowIndex + 2}.`,
              cellValue: rsaPin
            });
          } else {
            periodMap.set(currentEntry.period, currentEntry.rowIndex);
          }
        }

        if (inconsistentRows.size > 0) {
          const conflictingRows = Array.from(inconsistentRows).map(r => r + 2).join(', ');
          errors.push({
            cellRef: '',
            columnKey: scheduleHeaders.rsapin,
            rowIndex: firstEntry.rowIndex,
            errorMessage: `RSA PIN has inconsistent Name or Staff ID on row(s): ${conflictingRows}.`,
            cellValue: firstEntry.rsaPin
          });
        }
      });

      return errors;
    }
  ];
};
